'use client';
import React, { useEffect, useRef, useState } from 'react';
import styled, { useTheme } from '@benzinga/themetron';
//import { useCookie } from './hooks/useCookie';
import useInterval from './hooks/useInterval';
import { BreakingNews } from '@benzinga/basic-news-manager';
import Marquee from 'react-fast-marquee';
import Hooks from '@benzinga/hooks';
import { checkOverflow } from '@benzinga/frontend-utils';

export interface BreakingNewsBannerProps {
  data: BreakingNews[];
  onClose?: (nid: string) => void;
  progressBarColor?: string;
  textColor?: string;
}

export interface BannerText {
  link?: string;
  title: string;
}

const SECONDS = 5;

export const BreakingNewsBanner: React.FC<BreakingNewsBannerProps> = ({
  data,
  progressBarColor = '#2e4129',
  textColor = '#ffffff',
}) => {
  const [index, setIndex] = useState(0);
  const [shownEvent, setShownEvent] = useState<BreakingNews>(data[0]);
  const [progressBarTransitionDuration, setProgressBarTransitionDuration] = useState<number>(1);
  const [seconds, setSeconds] = useState(SECONDS);
  const [isOverflown, setIsOverflown] = useState(false);

  const theme = useTheme();

  const ref = useRef<HTMLDivElement>(null);
  const hiddenBannerRef = useRef<HTMLDivElement>(null);
  const bannerTextContainerRef = useRef<HTMLDivElement>(null);

  const isVisible = Hooks.usePageVisibility();

  useInterval(() => {
    if (data.length <= 1) return;
    const bannerTextContainer = bannerTextContainerRef.current;
    if (!bannerTextContainer) return;

    setShownEvent(data[index]);

    const fadeToNextItem = () => {
      if (isOverflown) setIsOverflown(false);
      bannerTextContainer.classList.remove('show');
      setTimeout(() => {
        setIndex((index + 1) % data.length);
        setShownEvent(data[index]);
      }, 500);
      setTimeout(() => {
        bannerTextContainer.classList.add('show');
        const element = hiddenBannerRef.current;
        if (!element) return;
        const isOverflowing = checkOverflow(element);
        if (isOverflowing) setIsOverflown(true);
      }, 1000);
    };

    if (isVisible) {
      if (progressBarTransitionDuration === 0) {
        setProgressBarTransitionDuration(1);
      }
      setSeconds(seconds - 1);
      if (seconds === 0) {
        setProgressBarTransitionDuration(0);
        setSeconds(SECONDS);
      } else if (seconds === 1) {
        fadeToNextItem();
      }
    }
  }, 1000);

  //const { height } = Hooks.useRefElementSize(desktopMenuWrapperRef);

  useEffect(() => {
    const element = hiddenBannerRef.current;
    if (!element) return;

    const isOverflowing = checkOverflow(element);
    setIsOverflown(isOverflowing);

    const onResize = () => {
      const isOverflowing = checkOverflow(element);
      setIsOverflown(isOverflowing);
    };
    window.addEventListener('resize', onResize);

    return () => window.removeEventListener('resize', onResize);
  }, []);

  const backgroundColors = {
    breaking: theme.colorPalette.red600,
    event: theme.colorPalette.green600,
    news: theme.colorPalette.blue500,
    notice: theme.colorPalette.orange500,
  };

  if (data.length === 0) return null;

  const progressBarWidth = Math.round(seconds * (100 / SECONDS));

  return (
    <div className="flex flex-col w-full breaking-news-banner" id="breaking-news-banner" ref={ref}>
      <BannerContainer
        className="flex flex-col h-12 relative shadow-md"
        style={{ backgroundColor: backgroundColors[shownEvent?.type || 'news'] }}
      >
        <div
          className="banner-text show flex font-bold h-full items-center justify-center w-full whitespace-nowrap"
          ref={bannerTextContainerRef}
          style={{ color: textColor }}
        >
          {isOverflown ? (
            <Marquee gradient={false} speed={70}>
              <div className="mx-4">{shownEvent.title}</div>
            </Marquee>
          ) : (
            <div>{shownEvent.title}</div>
          )}
        </div>
        {shownEvent.link && (
          <a
            className="block absolute w-full h-full top-0 left-0 cursor-pointer"
            data-action={`Breaking News Banner Click - ${shownEvent.type}`}
            href={shownEvent.link}
            rel="noreferrer"
            style={{ color: textColor, zIndex: 2 }}
            target="_blank"
          >
            <div className="sr-only">Visit {shownEvent.link}</div>
          </a>
        )}
        {data.length > 1 && (
          <ProgressBar
            duration={progressBarTransitionDuration}
            progressBarColor={progressBarColor}
            width={`${progressBarWidth}%`}
          />
        )}
      </BannerContainer>
      <StyledHiddenBanner className="hidden-banner" ref={hiddenBannerRef}>
        <div>{shownEvent.title}</div>
      </StyledHiddenBanner>
    </div>
  );
};

const BannerContainer = styled.div`
  .banner-text {
    transition: 1.2s opacity ease-out;
    opacity: 0;

    &.show {
      opacity: 1;
      overflow: hidden;
    }

    > div {
      overflow: hidden;
    }
  }
`;

const ProgressBar = styled.div<{ progressBarColor: string; duration: number; width: string }>`
  transition: width ${({ duration }: { duration: number }) => duration}s linear;
  width: ${({ width }) => (width ? width : '100%')};
  background-color: ${({ progressBarColor }) => progressBarColor};
  height: 0.25rem;
`;

const StyledHiddenBanner = styled.div`
  &.hidden-banner {
    position: absolute;
    font-weight: 700;
    white-space: nowrap;
    visibility: hidden;
    width: calc(100% - 32px);
    left: -999999px;
  }
`;
